import { router } from "@inertiajs/react";
import {
  CheckCircle,
  Clock,
  Copy,
  Eye,
  Link2,
  Mail,
  MessageSquareMore,
  RotateCcw,
  Users,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { HelpButton, HelpDialog } from "@/components/help-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useHelpDialog } from "@/hooks/use-help-dialog";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import { quoteInvitationPath } from "@/routes";
import type { QuoteInvitation } from "@/types";
import { formatDateTimeAR } from "@/utils/date";

interface InvitationsManagerProps {
  purchaseRequestId: number;
  purchaseRequestNumber: string;
  existingInvitations: QuoteInvitation[];
  onSendInvitations?: (supplierIds: number[]) => void;
  disabled?: boolean;
}

const statusConfig = {
  pending: {
    label: "Pendiente",
    color: "bg-yellow-100 text-yellow-800",
    icon: Clock,
  },
  sent: {
    label: "Enviado",
    color: "bg-blue-100 text-blue-800",
    icon: Mail,
  },
  viewed: {
    label: "Visto",
    color: "bg-purple-100 text-purple-800",
    icon: Eye,
  },
  responded: {
    label: "Respondido",
    color: "bg-green-100 text-green-800",
    icon: CheckCircle,
  },
};

export default function InvitationsManager({
  purchaseRequestId,
  purchaseRequestNumber,
  existingInvitations,
  disabled = false,
}: InvitationsManagerProps) {
  const routes = useOrganizationRoutes();
  const [resendingInvitations, setResendingInvitations] = useState<Set<number>>(
    new Set(),
  );
  const [copyingInvitations, setCopyingInvitations] = useState<Set<number>>(
    new Set(),
  );

  const invitationsHelp = useHelpDialog("invitationsManagement");

  const handleResendInvitation = (invitationId: number) => {
    setResendingInvitations((prev) => new Set(prev.add(invitationId)));

    router.post(
      routes.purchaseRequests.quoteInvitations.resend(
        purchaseRequestId,
        invitationId,
      ),
      {},
      {
        onFinish: () => {
          setResendingInvitations((prev) => {
            const next = new Set(prev);
            next.delete(invitationId);
            return next;
          });
        },
      },
    );
  };

  const handleCopyLinkAndMessage = async (invitation: QuoteInvitation) => {
    setCopyingInvitations((prev) => new Set(prev.add(invitation.id)));

    const invitationUrl = `${window.location.origin}${quoteInvitationPath(invitation.token)}`;
    const message = `Hola ${invitation.supplier.name}!

Estamos necesitando tu precio para la Solicitud #${purchaseRequestNumber}.

Por favor subí tu cotización acá 👉 ${invitationUrl}

¡Gracias!`;

    try {
      await navigator.clipboard.writeText(message);
      toast.success("Mensaje con enlace copiado");
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      toast.error("No se pudo copiar el mensaje");
    } finally {
      setCopyingInvitations((prev) => {
        const next = new Set(prev);
        next.delete(invitation.id);
        return next;
      });
    }
  };

  const handleCopyOnlyLink = async (invitation: QuoteInvitation) => {
    setCopyingInvitations((prev) => new Set(prev.add(invitation.id)));
    const invitationUrl = `${window.location.origin}${quoteInvitationPath(invitation.token)}`;
    try {
      await navigator.clipboard.writeText(invitationUrl);
      toast.success("Enlace copiado");
    } catch (error) {
      console.error("Error copying link to clipboard:", error);
      toast.error("No se pudo copiar el enlace");
    } finally {
      setCopyingInvitations((prev) => {
        const next = new Set(prev);
        next.delete(invitation.id);
        return next;
      });
    }
  };

  const normalizePhoneForWhatsApp = (
    phone: string | null | undefined,
  ): string => {
    if (!phone) return "";

    if (phone.startsWith("+")) {
      // Remove + and clean the rest
      return phone.substring(1).replace(/\D/g, "").replace(/^0+/, "").trim();
    }

    // Clean the phone number: remove non-digits and leading zeros
    const cleanPhone = phone.replace(/\D/g, "").replace(/^0+/, "").trim();

    return cleanPhone.startsWith("54") ? cleanPhone : "54" + cleanPhone;
  };

  const openWhatsAppMessage = (
    invitation: QuoteInvitation,
    purchaseRequestNumber: string,
  ) => {
    const normalizedPhone = normalizePhoneForWhatsApp(
      invitation.supplier.phone,
    );

    const invitationUrl = `${window.location.origin}${quoteInvitationPath(invitation.token)}`;
    const message = encodeURIComponent(
      `Hola ${invitation.supplier.name}!\n\nEstamos necesitando tu precio para la Solicitud #${purchaseRequestNumber}.\n\nPor favor subí tu cotización acá: ${invitationUrl}\n\n¡Gracias!`,
    );
    const waUrl = `https://wa.me/${normalizedPhone}?text=${message}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");
  };

  const getStatusInfo = (invitation: QuoteInvitation) => {
    const config = statusConfig[invitation.status];
    const IconComponent = config.icon;
    return { config, IconComponent };
  };

  if (existingInvitations.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Gestión de Invitaciones
              </CardTitle>
              <CardDescription>
                Administra las invitaciones enviadas a proveedores para esta
                solicitud
              </CardDescription>
            </div>
            <HelpButton
              onClick={invitationsHelp.open}
              label="Ayuda"
              variant="outline"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="mb-2">No hay invitaciones enviadas aún</p>
            <p className="text-sm">
              Usa el botón &quot;Enviar Solicitud de Cotización&quot; para
              invitar proveedores
            </p>
          </div>
        </CardContent>
        <HelpDialog
          content={invitationsHelp.content}
          open={invitationsHelp.isOpen}
          onOpenChange={invitationsHelp.setIsOpen}
        />
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Gestión de Invitaciones
            </CardTitle>
            <CardDescription>
              {existingInvitations.length} invitación
              {existingInvitations.length !== 1 ? "es" : ""} enviada
              {existingInvitations.length !== 1 ? "s" : ""}
            </CardDescription>
          </div>
          <HelpButton
            onClick={invitationsHelp.open}
            label="Ayuda"
            variant="outline"
          />
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Proveedor</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Enviado</TableHead>
                <TableHead>Último Visto</TableHead>
                <TableHead>Respondido</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {existingInvitations.map((invitation) => {
                const { config, IconComponent } = getStatusInfo(invitation);
                const isResending = resendingInvitations.has(invitation.id);

                return (
                  <TableRow key={invitation.id}>
                    <TableCell className="font-medium">
                      <div>
                        <p>{invitation.supplier.name}</p>
                        {(invitation.supplier.email ??
                          invitation.supplier.phone) && (
                          <p className="text-sm text-muted-foreground">
                            {invitation.supplier.email ??
                              invitation.supplier.phone}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={config.color}>
                        <IconComponent className="h-3 w-3 mr-1" />
                        {config.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {formatDateTimeAR(invitation.last_sent_at ?? "")}
                    </TableCell>
                    <TableCell>
                      {formatDateTimeAR(invitation.viewed_at ?? "")}
                    </TableCell>
                    <TableCell>
                      {formatDateTimeAR(invitation.last_submitted_at ?? "")}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <div className="divide-primary-foreground/30 inline-flex w-fit divide-x rounded-md shadow-xs">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                aria-label="Copiar mensaje completo con instrucciones y enlace"
                                variant="outline"
                                size="sm"
                                className="relative overflow-hidden rounded-none rounded-s-md shadow-none before:absolute before:inset-0 before:rounded-[inherit] before:bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.35)_50%,transparent_75%,transparent_100%)] before:bg-[length:250%_250%,100%_100%] before:bg-[position:200%_0,0_0] before:bg-no-repeat before:transition-[background-position_0s_ease] before:duration-1000 hover:before:bg-[position:-100%_0,0_0] focus-visible:z-10 dark:before:bg-[linear-gradient(45deg,transparent_25%,rgba(0,0,0,0.2)_50%,transparent_75%,transparent_100%)]"
                                onClick={() => {
                                  void handleCopyLinkAndMessage(invitation);
                                }}
                                disabled={
                                  disabled ||
                                  copyingInvitations.has(invitation.id)
                                }
                              >
                                {copyingInvitations.has(invitation.id) ? (
                                  <>
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-2"></div>
                                    Copiando...
                                  </>
                                ) : (
                                  <>
                                    <Copy className="h-3 w-3 mr-2" />
                                    Mensaje + enlace
                                  </>
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              Copia un mensaje listo para WhatsApp/Email con
                              instrucciones y el enlace.
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                aria-label="Copiar solo el enlace directo a la invitación"
                                variant="outline"
                                size="sm"
                                className="relative overflow-hidden rounded-none rounded-e-md shadow-none before:absolute before:inset-0 before:rounded-[inherit] before:bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.35)_50%,transparent_75%,transparent_100%)] before:bg-[length:250%_250%,100%_100%] before:bg-[position:200%_0,0_0] before:bg-no-repeat before:transition-[background-position_0s_ease] before:duration-1000 hover:before:bg-[position:-100%_0,0_0] focus-visible:z-10 dark:before:bg-[linear-gradient(45deg,transparent_25%,rgba(0,0,0,0.2)_50%,transparent_75%,transparent_100%)]"
                                onClick={() => {
                                  void handleCopyOnlyLink(invitation);
                                }}
                                disabled={
                                  disabled ||
                                  copyingInvitations.has(invitation.id)
                                }
                              >
                                {copyingInvitations.has(invitation.id) ? (
                                  <>
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-2"></div>
                                    Copiando...
                                  </>
                                ) : (
                                  <>
                                    <Link2 className="h-3 w-3 mr-2" />
                                    Solo enlace
                                  </>
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              Copia únicamente el enlace directo a la
                              invitación.
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                window.alert(
                                  `Se abrira una nueva pestaña donde podra enviar un mensaje a ${invitation.supplier.name} a su numero ${invitation.supplier.phone}.`,
                                );
                                openWhatsAppMessage(
                                  invitation,
                                  purchaseRequestNumber,
                                );
                              }}
                              disabled={disabled || !invitation.supplier.phone}
                            >
                              <>
                                <MessageSquareMore className="h-3 w-3 mr-2" />
                                Enviar por WhatsApp
                              </>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Se abrira una nueva pestaña donde podra enviar un
                            mensaje via WhatsApp Web. Debe estar registrado.
                          </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                window.alert(
                                  `Se reenviará la invitación por email a ${invitation.supplier.name}.\n\nSi no lo recibe, pedile que revise la carpeta de spam.`,
                                );
                                handleResendInvitation(invitation.id);
                              }}
                              disabled={
                                disabled ||
                                isResending ||
                                invitation.status === "pending"
                              }
                            >
                              {isResending ? (
                                <>
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-2"></div>
                                  Enviando...
                                </>
                              ) : (
                                <>
                                  <RotateCcw className="h-3 w-3 mr-2" />
                                  Reenviar
                                </>
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Se reenviará por email la invitación al proveedor.
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
      <HelpDialog
        content={invitationsHelp.content}
        open={invitationsHelp.isOpen}
        onOpenChange={invitationsHelp.setIsOpen}
      />
    </Card>
  );
}
